import { LitElement, html, css, nothing } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { styleMap } from "lit/directives/style-map.js";
import { customElement, property, query, state } from "lit/decorators.js";
import { resolveColor } from "../helper/utils/color-resolver";
import { PresetController } from "../helper/controllers/preset-controller";
import { live } from "lit/directives/live.js";
import { FormControlBase } from "../helper/internals/form-control-base";
import { SkyIcon } from "../sky-icon/sky-icon.js";

export type SkyInputValue = string | number | File[] | null;
export type SkyInputType = string;
export type SkyInputErrorDisplayMode = "none" | "single" | "all";
export type SkyInputIconPosition = "left" | "right";
export type SkyInputValidation = (value: string | File[]) => true | string;
export type SkyInputValueChangedDetail = { value: string | number };
export type SkyInputValueClearedDetail = { value: string | number };
export type SkyInputFileSelectedDetail = {
  files: File[];
  fileName: string;
  totalSize: number;
  fileCount: number;
};
export type SkyInputValidationErrorDetail = { errors: string[] };
export type SkyInputIconClickDetail = {
  position: SkyInputIconPosition;
  eventType: string | null;
  eventData: Event | null;
};

const EMPTY_VALIDATIONS: SkyInputValidation[] = Object.freeze(
  [],
) as unknown as SkyInputValidation[];

function samePrimitiveArray(a: unknown[], b: unknown[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function inputValidationsHasChanged(value: unknown, old: unknown): boolean {
  const next = Array.isArray(value) ? value : EMPTY_VALIDATIONS;
  const prev = Array.isArray(old) ? old : EMPTY_VALIDATIONS;
  if (next === prev) return false;
  if (next.length === 0 && prev.length === 0) return false;
  return true;
}

/**
 * @element sky-input
 *
 * @summary Versatile form input supporting text, password, number, textarea, and file workflows.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/input
 * @dependency sky-icon
 *
 * @uiVModel value value-changed
 *
 * @slot icon-left - Optional icon/content placed in the left slot.
 * @slot icon-right - Optional icon/content placed in the right slot.
 * @property {SkyInputValue} value - Current input value.
 * @property {string} placeholder - Placeholder text.
 * @property {string} label - Label text. Above the field by default; on the border for `fieldset`; inside the field for `inside`.
 * @property {string} variant - Visual style: `default`, `highlight`, `fieldset`, or `inside`.
 * @property {SkyInputType} type - Input type (`text`, `password`, `number`, `textarea`, `file`, etc.).
 * @property {string} suffix - Optional suffix label fragment.
 * @property {string} prefix - Optional prefix label fragment.
 * @property {string} prefixColor - Prefix color override.
 * @property {string} suffixColor - Suffix color override.
 * @property {string} color - Accent color token/value.
 * @property {SkyInputValidation[]} validations - Validation functions.
 * @property {boolean} compact - Compact display mode.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @property {boolean} loading - Loading state.
 * @property {boolean} showErrors - Enables error rendering.
 * @property {boolean} clearable - Enables clear button.
 * @property {SkyInputErrorDisplayMode} errorDisplayMode - Error rendering mode.
 * @property {boolean} showNumberButtons - Enables number increment/decrement controls.
 * @property {boolean} showPasswordToggle - Enables password visibility toggle.
 * @property {string} passwordVisibleIcon - Icon used when password is visible.
 * @property {string} passwordHiddenIcon - Icon used when password is hidden.
 *
 * @fires {CustomEvent<SkyInputValueChangedDetail>} value-changed - Fired when value changes.
 * @fires {CustomEvent<SkyInputValueClearedDetail>} value-cleared - Fired after clear action.
 * @fires {CustomEvent<SkyInputFileSelectedDetail>} file-selected - Fired after file selection changes.
 * @fires {CustomEvent<SkyInputValidationErrorDetail>} validation-error - Fired after validations run.
 * @fires {CustomEvent<SkyInputIconClickDetail>} left-icon-click - Fired when left icon slot is clicked.
 * @fires {CustomEvent<SkyInputIconClickDetail>} right-icon-click - Fired when right icon slot is clicked.
 *
 * @method validateInput Runs configured validations and updates invalid state.
 * @method handleClear Clears value and emits corresponding events.
 * @method formatFileSize Formats byte size for UI display.
 * @method focusInput Focuses the underlying native input or textarea.
 *
 * @csspart input-container - The outer container wrapping label and input wrapper.
 * @csspart label - The label element above the input.
 * @csspart input-wrapper - The wrapper around input/textarea and icons.
 * @csspart icon-slot - Base class for left/right icon slots.
 * @csspart icon-slot-left - Left icon slot.
 * @csspart icon-slot-right - Right icon slot.
 * @csspart clear-button - The clear/reset button.
 * @csspart toggle-password - The toggle icon for password visibility.
 * @csspart loading-indicator - The spinner shown when loading.
 * @csspart error-list - UL element containing validation errors.
 * @csspart error - Individual error message.
 * @csspart file-info - File count and size info below the file input.
 * @csspart input-prefix - Inline prefix fragment inside the field.
 * @csspart input-suffix - Inline suffix fragment inside the field.
 *
 * @Behavior
 * - Extends `FormControlBase` for automatic form association and validation integration
 * - Supports all standard HTML input types with enhanced styling and functionality
 * - File inputs with multi-file support, preview, and size tracking
 * - Automatic validation integration with parent forms via `sky-form`
 *
 * - **none**: No error messages displayed
 * - **single**: Show only the first error message
 * - **all**: Show all validation error messages
 *
 * @example
 * ```html
 * <sky-input
 *   label="Password"
 *   type="password"
 *   placeholder="Enter your password"
 *   clearable
 *   show-password-toggle
 *   required
 * >
 *   <sky-icon slot="icon-left" icon="ion:lock-closed"></sky-icon>
 * </sky-input>
 * ```
 * ```vue
 * <template>
 *   <sky-input label="Password" type="password" clearable show-password-toggle required>
 *     <sky-icon slot="icon-left" icon="ion:lock-closed"></sky-icon>
 *   </sky-input>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-input label="Password" type="password" clearable show-password-toggle required>
 *       <sky-icon slot="icon-left" icon="ion:lock-closed"></sky-icon>
 *     </sky-input>
 *   );
 * }
 * ```
 */
@customElement("sky-input")
export class SkyInput extends FormControlBase {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  @property({ type: String }) value: SkyInputValue = "";
  @property({ type: String }) placeholder = "";
  @property({ type: String }) label = "";
  @property({ type: String }) type: SkyInputType = "text";
  @property({ type: String }) suffix = "";
  @property({ type: String }) prefix = "";
  @property({ type: String }) prefixColor = "";
  @property({ type: String }) suffixColor = "";
  @property({ type: String }) color = "primary";
  private _validations: SkyInputValidation[] = EMPTY_VALIDATIONS;
  @property({ type: Array, hasChanged: inputValidationsHasChanged })
  get validations(): SkyInputValidation[] {
    return this._validations;
  }
  set validations(value: SkyInputValidation[] | null | undefined) {
    this._validations =
      Array.isArray(value) && value.length > 0 ? value : EMPTY_VALIDATIONS;
  }
  @property({ type: Boolean, reflect: true }) compact = false;
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ type: Boolean }) showErrors = false;
  @property({ type: Boolean, reflect: true }) clearable = false;
  @property({ type: String }) errorDisplayMode: SkyInputErrorDisplayMode = "single";
  @property({ type: Boolean, reflect: true }) override required = false;
  @property({ type: String }) step: string | null = null;
  @property({ type: String }) min: string | null = null;
  @property({ type: String }) max: string | null = null;
  @property({ type: String }) override autocapitalize = "off";
  @property({ type: String }) autocomplete = "off";
  @property({ type: Boolean }) override spellcheck = false;
  @property({ type: String }) override dir = "ltr";
  @property({ type: Number }) rows = 6;
  @property({ type: String }) resizable = "none";
  @property({ type: String }) accept = "";
  @property({ type: Boolean }) multiple = false;
  @property({ type: String }) inputmode: string | null = null;
  @property({ type: String }) enterkeyhint: string | null = null;
  @property({ type: String }) pattern: string | null = null;
  @property({ type: Number }) minlength: number | null = null;
  @property({ type: Number }) maxlength: number | null = null;
  @property({ type: String }) list: string | null = null;
  @property({ type: Number }) size: number | null = null;
  @property({ type: Number }) cols: number | null = null;
  @property({ type: Boolean }) autofocus = false;
  @property({ type: Boolean }) capture = false;
  @property({ type: String }) inputRole: string | null = null;
  @property({ type: String }) ariaLabel: string | null = null;
  @property({ type: String }) ariaLabelledby: string | null = null;
  @property({ type: String }) ariaDescribedby: string | null = null;
  @property({ type: Boolean }) showNumberButtons = false;
  @property({ type: Boolean }) showPasswordToggle = true;
  @property({ type: String }) passwordVisibleIcon = "material-symbols:visibility-off";
  @property({ type: String }) passwordHiddenIcon = "material-symbols:visibility";
  @property({ type: Boolean, reflect: true }) validationActive = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ type: String, reflect: true }) variant:
    | "default"
    | "highlight"
    | "fieldset"
    | "inside" = "default";

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  @state() private selectedFileName = "";
  @state() private selectedFiles: File[] = [];
  @state() private focused = false;
  @state() private validationStatus: boolean[] = [];
  @state() private validationErrors: string[] = [];
  @state() private typingStarted = false;
  @state() private autofillIconSlot = "left";
  @state() private autofillGuardActive = false;
  @state() private showPassword = false;
  @state() private totalFileSize = 0;
  @state() private selectedFileCount = 0;
  @state() private filePreview: string | ArrayBuffer | null = null;

  @query(".input-wrapper") private inputWrapperEl!: HTMLElement;
  @query("input, textarea") private nativeInputEl!:
    | HTMLInputElement
    | HTMLTextAreaElement;

  private _leftIconSlot: HTMLSlotElement | null = null;
  private _rightIconSlot: HTMLSlotElement | null = null;
  private _iconClickWired = new WeakSet<HTMLElement>();
  private _onLeftIconSlotChange = () =>
    this._wireIconSlot("left", this._leftIconSlot);
  private _onRightIconSlotChange = () =>
    this._wireIconSlot("right", this._rightIconSlot);

  static override styles = css`
    :host {
      display: block;
      font-family: var(--sky-font);
      width: 100%;
      /* Control size */
      --sky-input-height: 36px;
      --sky-input-height-compact: 28px;
      --sky-input-border-width: 1px;
      /* Spacing scale — tight internal joins, softer outer edge */
      --sky-input-pad-x: var(--sky-space-3, 12px);
      --sky-input-pad-x-sm: var(--sky-space-1, 4px);
      --sky-input-pad-x-xs: 0px;
      --sky-input-pad-y: var(--sky-space-1, 4px);
      --sky-input-pad-y-sm: 0px;
      --sky-input-gap: var(--sky-space-1, 4px);
      --sky-input-icon-width: 2rem;
    }

    :host([data-sky-compact]) {
      --sky-input-pad-x: var(--sky-space-2, 8px);
      --sky-input-pad-x-sm: var(--sky-space-1, 4px);
      --sky-input-icon-width: 1.75rem;
    }

    /* Compact typography + denser chrome for every variant */
    :host([data-sky-compact]) .input-container > .label {
      font-size: var(--sky-font-size-xs);
      margin-bottom: var(--sky-space-1, 4px);
      line-height: 1.2;
    }

    :host([data-sky-compact]) input,
    :host([data-sky-compact]) textarea,
    :host([data-sky-compact]) .input-affix,
    :host([data-sky-compact]) .custom-file-label__text {
      font-size: var(--sky-font-size-sm);
    }

    :host([data-sky-compact]) .icon-slot {
      font-size: var(--sky-font-size-lg);
    }

    :host([data-sky-compact]) .toggle-password,
    :host([data-sky-compact]) .clear-button {
      font-size: var(--sky-font-size-md);
    }

    :host([data-sky-compact]) .custom-file-label {
      border-radius: var(--sky-radius-primary);
    }

    :host([disabled]) .input-container {
      opacity: var(--sky-opacity-disabled, 0.6);
    }
    :host([disabled]) * {
      pointer-events: none;
    }

    :host([data-sky-variant="highlight"]){
      .label{
        color: var(--sky-input-color);
      }
    }

    /* Fieldset / legend: native fieldset+legend; wrapper keeps solid fill */
    :host([data-sky-variant="fieldset"]) {
      overflow: visible;
    }

    :host([data-sky-variant="fieldset"]) .input-container {
      overflow: visible;
      /* Room for legend text sitting on the top border — keep off :host */
      padding-block-start: 0.65em;
    }

    :host([data-sky-variant="fieldset"]) fieldset.input-wrapper {
      display: block;
      height: auto;
      margin: 0;
      min-inline-size: 0;
      min-width: 0;
      padding: 0;
      overflow: visible;
      background: var(--sky-glass-solid);
      box-shadow: none;
    }

    :host([data-sky-variant="fieldset"]) fieldset.input-wrapper[compact] {
      height: auto;
    }

    :host([data-sky-variant="fieldset"]) .input-fieldset-body {
      display: flex;
      width: 100%;
      align-items: center;
      box-sizing: border-box;
      /* Match default control outer height once fieldset border is included */
      height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      background: transparent;
      border-radius: inherit;
      min-width: 0;
      overflow: hidden;
    }

    :host([data-sky-variant="fieldset"]) fieldset.input-wrapper[compact] .input-fieldset-body {
      height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
    }

    :host([data-sky-variant="fieldset"]) fieldset.input-wrapper:has(textarea) {
      height: auto;
    }

    :host([data-sky-variant="fieldset"]) fieldset.input-wrapper:has(textarea) .input-fieldset-body {
      height: calc((var(--rows)) * 23px);
      min-height: calc((var(--rows)) * 23px);
      align-items: flex-start;
    }

    :host([data-sky-variant="fieldset"]) legend.label {
      float: unset;
      width: auto;
      max-width: calc(100% - 2 * var(--sky-input-pad-x));
      margin-inline-start: var(--sky-input-pad-x);
      margin-block: 0;
      padding-inline: var(--sky-input-pad-x-sm);
      padding-block: 0;
      border: none;
      background: transparent;
      background-color: transparent;
      color: var(--sky-text-secondary);
      font-size: var(--sky-font-size-xs);
      /* Take no in-flow space; inner text is centered on the border notch */
      height: 0;
      line-height: 0;
      overflow: visible;
      white-space: nowrap;
      pointer-events: none;
    }

    :host([data-sky-variant="fieldset"]) legend.label .legend-text {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
      transform: translateY(-50%);
    }

    :host([data-sky-variant="fieldset"]) .input-container:focus-within legend.label {
      color: var(--sky-input-color);
      font-size: var(--sky-font-size-xs);
    }

    /* Focus via real border — ::after would paint over the legend gap */
    :host([data-sky-variant="fieldset"]) .input-wrapper::after {
      content: none;
      display: none;
    }

    :host([data-sky-variant="fieldset"]) .input-wrapper:has(input:focus),
    :host([data-sky-variant="fieldset"]) .input-wrapper:has(input:focus-visible),
    :host([data-sky-variant="fieldset"]) .input-wrapper:has(textarea:focus),
    :host([data-sky-variant="fieldset"]) .input-wrapper:has(textarea:focus-visible) {
      border-color: var(--sky-input-color);
    }

    :host([data-sky-variant="fieldset"][validationActive]:invalid) .input-wrapper {
      border-color: var(--sky-danger-primary);
    }

    :host([data-sky-variant="fieldset"][validationActive]:invalid) legend.label {
      color: var(--sky-danger-primary);
    }

    :host([data-sky-variant="fieldset"][compact]) legend.label,
    :host([data-sky-variant="fieldset"][compact]) .input-container:focus-within legend.label {
      font-size: var(--sky-font-size-xs);
    }

    /* Inside: label lives in the wrapper above the value row */
    :host([data-sky-variant="inside"]) {
      --sky-input-height-inside: 40px;
      /* Two lines (label + value) need a bit more than single-line compact */
      --sky-input-height-inside-compact: 36px;
      --sky-input-inside-label-block: 2px;
      --sky-input-inside-value-min: 1.15em;
    }

    :host([data-sky-variant="inside"]) .input-wrapper {
      height: auto;
      min-height: var(--sky-input-height-inside);
      align-items: center;
      padding-block: var(--sky-input-pad-y-sm, 0px);
    }

    :host([data-sky-variant="inside"]) .input-wrapper[compact] {
      height: auto;
      min-height: var(--sky-input-height-inside-compact);
      padding-block: var(--sky-input-pad-y-sm, 0px);
    }

    :host([data-sky-variant="inside"]) .input-inside-main {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0;
      padding-block: var(--sky-input-inside-label-block);
    }

    :host([data-sky-variant="inside"]) .input-inside-main > .label {
      margin: 0;
      padding-inline: var(--sky-input-pad-x);
      font-size: var(--sky-font-size-xs);
      line-height: 1.15;
      min-height: 0;
      color: var(--sky-text-secondary);
      pointer-events: none;
      overflow: visible;
      text-overflow: unset;
      flex-shrink: 0;
    }

    :host([data-sky-variant="inside"][compact]) .input-inside-main > .label {
      font-size: 0.6875rem; /* ~11px — denser than xs for two-line compact */
      line-height: 1.15;
      min-height: 0;
    }

    :host([data-sky-variant="inside"][compact]) .input-inside-row input,
    :host([data-sky-variant="inside"][compact]) .input-inside-row textarea {
      font-size: var(--sky-font-size-sm);
      line-height: 1.15;
    }

    :host([data-sky-variant="inside"]) .input-container:focus-within .input-inside-main > .label {
      color: var(--sky-input-color);
      font-size: var(--sky-font-size-xs);
    }

    :host([data-sky-variant="inside"][compact]) .input-container:focus-within .input-inside-main > .label {
      font-size: 0.6875rem;
    }

    :host([data-sky-variant="inside"][validationActive]:invalid) .input-inside-main > .label {
      color: var(--sky-danger-primary);
    }

    :host([data-sky-variant="inside"]) .input-inside-row {
      display: flex;
      align-items: center;
      min-width: 0;
      width: 100%;
      min-height: var(--sky-input-inside-value-min);
    }

    :host([data-sky-variant="inside"]) .input-wrapper:has(.icon-slot[filled].left) .input-inside-main > .label,
    :host([data-sky-variant="inside"]) .input-wrapper:has(.input-affix.prefix) .input-inside-main > .label {
      padding-inline-start: 0;
    }

    :host([data-sky-variant="inside"]) .input-inside-row input,
    :host([data-sky-variant="inside"]) .input-inside-row textarea {
      padding-block: 0;
      height: auto;
      min-height: 0;
      line-height: 1.2;
    }

    :host([data-sky-variant="inside"]) .input-wrapper:has(textarea) {
      height: auto;
      min-height: var(--sky-input-height-inside);
      align-items: flex-start;
    }

    :host([data-sky-variant="inside"]) .icon-slot,
    :host([data-sky-variant="inside"]) .icon-slot[filled] {
      align-self: center;
      height: auto;
      max-height: none;
      align-items: center;
      justify-content: center;
    }

    .input-container {
      position: relative;
      display: flex;
      flex-direction: column;
      width: inherit;
      cursor: text;
    }

    sky-icon {
      display: inline-block;
      width: 1em;
      height: 1em;
    }

    /* Outside labels only — never bump fieldset/inside fonts (clips legends) */
    .input-container:focus-within > .label {
      color: var(--sky-input-color);
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    :host([data-sky-compact]) .input-container:focus-within > .label {
      font-size: var(--sky-font-size-xs);
    }

    .label {
      font-size: var(--sky-font-size-sm);
      margin-bottom: var(--sky-space-1, 4px);
      text-align: start;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: var(--sky-font);
      line-height: 1.35;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      width: 100%;
      align-items: center;
      box-sizing: border-box;
      height: var(--sky-input-height);
      min-height: var(--sky-input-height);
      pointer-events: auto;
      background: var(--sky-glass-solid);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border-radius: var(--sky-radius-secondary);
      border: var(--sky-border-primary);
      box-shadow: var(--sky-box-shadow-primary);
      transition:
        border-color var(--sky-transition-normal, 200ms ease),
        box-shadow var(--sky-transition-normal, 200ms ease);
    }

    .input-wrapper::after {
      content: "";
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: inherit;
      border: 1px solid transparent;
      border-bottom-color: transparent;
      opacity: 0;
      transition:
        opacity 0.3s ease,
        border-color 0.3s ease;
      pointer-events: none;
    }

    .input-wrapper:has(input:focus-visible)::after,
    .input-wrapper:has(textarea:focus-visible)::after {
      opacity: 1;
      border-bottom-color: var(--sky-input-color);
    }

    .input-wrapper:has(input:focus)::after,
    .input-wrapper:has(textarea:focus)::after {
      opacity: 1;
      border-bottom-color: var(--sky-input-color);
    }

    :host([validationActive]:invalid) .input-wrapper::after {
      opacity: 1;
      border-bottom-color: var(--sky-danger-primary);
    }
    :host([validationActive]:invalid) .label {
      color: var(--sky-danger-primary);
    }

    :host([validationActive]:invalid) .icon-slot {
      color: var(--sky-danger-primary);
    }

    .input-wrapper[compact] {
      height: var(--sky-input-height-compact);
      min-height: var(--sky-input-height-compact);
      border: var(--sky-border-primary);
      border-radius: var(--sky-radius-primary);
    }

    input[type="date"],
    input[type="time"],
    input[type="week"],
    input[type="month"],
    input[type="datetime-local"] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      background: none;
      border: none;
      font-family: inherit;
    }

    input[type="date"]::-webkit-calendar-picker-indicator,
    input[type="time"]::-webkit-calendar-picker-indicator,
    input[type="week"]::-webkit-calendar-picker-indicator,
    input[type="month"]::-webkit-calendar-picker-indicator,
    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
      display: none;
    }

    .input-wrapper:has(input:focus)[compact]::after {
      border: 1px solid var(--sky-input-color);
    }

    .toggle-password[compact] {
      font-size: var(--sky-font-size-md);
    }

    input {
      font-family: var(--sky-font);
      font-size: var(--sky-font-size-md);
      line-height: 1.2;
      text-align: left;
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    input:-webkit-autofill {
      -webkit-text-fill-color: var(--sky-text-secondary, inherit) !important;
      transition: background-color 9999s ease-in-out 0s !important;
    }

    :dir(rtl) input {
      text-align: right;
    }

    input[type="number"] {
      appearance: textfield;
      -moz-appearance: textfield;
    }

    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .number-btn {
      display: flex;
      flex-shrink: 0;
      margin-inline-end: var(--sky-input-pad-x-sm);
      gap: var(--sky-input-gap);
    }

    :dir(rtl).number-btn {
      margin-inline-end: var(--sky-input-pad-x-sm);
    }

    .file-info {
      display: flex;
      justify-content: flex-end;
      font-size: var(--sky-font-size-xs);
      color: var(--sky-text-secondary);
      font-family: var(--sky-font);
    }

    :dir(rtl).file-info {
      flex-direction: row-reverse;
    }

    .increment-btn,
    .decrement-btn {
      background: var(--sky-glass-solid);
      color: var(--sky-text-primary);
      border: var(--sky-border-primary);
      border-radius: var(--sky-radius-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--sky-input-pad-x-xs) var(--sky-input-pad-x-sm);
      font-size: var(--sky-font-size-sm);
      cursor: pointer;
      transition: color var(--sky-transition-normal, 200ms ease);
      font-family: var(--sky-font);
    }

    .increment-btn:hover,
    .decrement-btn:hover {
      color: var(--sky-input-color);
    }

    .toggle-password {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-inline-end: var(--sky-input-pad-x-sm);
      font-size: var(--sky-font-size-lg);
      color: var(--sky-text-tertiary);
      cursor: pointer;
    }

    .toggle-password:hover {
      color: var(--sky-input-color);
    }

    input {
      flex: 1 1 auto;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      box-sizing: border-box;
      color: var(--sky-text-primary);
      padding-block: 0;
      padding-inline: var(--sky-input-pad-x);
    }

    input::placeholder {
      color: var(--sky-text-tertiary);
    }

    .input-affix {
      flex: 0 0 auto;
      font-family: var(--sky-font);
      font-size: var(--sky-font-size-md);
      font-weight: var(--sky-font-weight-medium);
      color: var(--sky-text-secondary);
      user-select: none;
      white-space: nowrap;
      line-height: 1;
    }

    .input-affix.prefix {
      padding-inline-start: var(--sky-input-pad-x);
      padding-inline-end: var(--sky-input-pad-x-sm);
    }

    .input-affix.suffix {
      padding-inline-start: var(--sky-input-pad-x-sm);
      padding-inline-end: var(--sky-input-pad-x);
    }

    .input-wrapper[compact] .input-affix {
      font-size: var(--sky-font-size-sm);
    }

    .input-wrapper:has(.input-affix.prefix) input,
    .input-wrapper:has(.input-affix.prefix) textarea {
      padding-inline-start: 0;
    }

    .input-wrapper:has(.icon-slot[filled].left) input,
    .input-wrapper:has(.icon-slot[filled].left) textarea {
      padding-inline-start: 0;
    }

    .input-wrapper:has(.icon-slot[filled].right) input,
    .input-wrapper:has(.icon-slot[filled].right) textarea,
    .input-wrapper:has(.clear-button) input,
    .input-wrapper:has(.clear-button) textarea,
    .input-wrapper:has(.toggle-password) input,
    .input-wrapper:has(.toggle-password) textarea,
    .input-wrapper:has(.number-btn) input,
    .input-wrapper:has(.loading-indicator) input {
      padding-inline-end: 0;
    }

    .input-wrapper:has(.input-affix.suffix) input,
    .input-wrapper:has(.input-affix.suffix) textarea {
      padding-inline-end: 0;
    }

    .custom-file-label__text {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--sky-text-tertiary);
      padding-inline-end: var(--sky-input-pad-x);
    }

    .custom-file-label:not(:has(.input-affix.prefix)) .custom-file-label__text {
      padding-inline-start: var(--sky-input-pad-x);
    }

    .custom-file-label__text[data-filled] {
      color: var(--sky-text-primary);
    }

    .icon-slot {
      display: none;
      justify-content: center;
      font-size: var(--sky-font-size-xl);
      align-items: center;
      color: var(--sky-text-primary);
      height: inherit;
      max-height: 100%;
      box-sizing: border-box;
      flex: 0 0 var(--sky-input-icon-width);
      width: var(--sky-input-icon-width);
      min-width: var(--sky-input-icon-width);
      padding: 0;
      background: none;
      border: none;
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    .input-wrapper:has(textarea) .icon-slot {
      max-height: var(--sky-input-height);
    }

    .icon-slot[filled] {
      display: flex;
      flex-shrink: 0;
      cursor: pointer;
    }

    .icon-slot[filled]:hover,
    .icon-slot[filled]:active,
    .icon-slot.focused {
      color: var(--sky-input-color);
    }

    .progress-message {
      pointer-events: none;
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: var(--sky-font-size-sm);
      margin-inline-start: var(--sky-input-pad-x-sm);
      font-family: var(--sky-font);
    }

    :dir(rtl) .progress-message {
      margin-inline-start: unset;
      margin-inline-end: var(--sky-input-pad-x-sm);
    }

    .error-list {
      list-style: none;
      margin: 0;
      padding: 0;
      width: 100%;
    }

    .error {
      font-size: var(--sky-font-size-xs);
      color: var(--sky-danger-primary);
      text-align: start;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: var(--sky-font);
    }

    ::slotted(.icon-slot) {
      display: block;
    }

    .input-wrapper[loading] input {
      cursor: wait;
    }

    .loading-indicator {
      flex-shrink: 0;
      margin-inline-end: var(--sky-input-pad-x-sm);
      width: 16px;
      height: 16px;
      border: 2px solid var(--sky-hover-primary);
      border-top: 2px solid var(--sky-input-color);
      border-radius: var(--sky-radius-full);
      animation: spin 1s linear infinite;
    }

    .loading-indicator[compact] {
      width: 10px;
      height: 10px;
    }

    .input-wrapper[loading] .icon-slot.right {
      display: none;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .clear-button {
      flex-shrink: 0;
      margin-inline-end: var(--sky-input-pad-x-sm);
      border: none;
      font-size: var(--sky-font-size-xl);
      color: var(--sky-input-color);
      background-color: var(--sky-hover-primary);
      border-radius: var(--sky-radius-full);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      cursor: pointer;
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    .clear-button[compact] {
      font-size: var(--sky-font-size-md);
    }

    .clear-button[loading] {
      opacity: 0.6;
      pointer-events: none;
    }

    input[type="file"] {
      position: absolute;
      width: 100%;
      height: 100%;
      opacity: 0;
      pointer-events: auto;
      z-index: -1;
    }

    .custom-file-label {
      position: relative;
      flex: 1;
      display: flex;
      align-items: center;
      gap: var(--sky-input-gap);
      text-align: left;
      padding: 0;
      color: var(--sky-input-color);
      border-radius: var(--sky-radius-secondary);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      cursor: pointer;
      transition:
        background 0.3s ease,
        padding 0.3s ease;
      font-family: var(--sky-font);
    }

    .custom-file-label:hover .custom-file-label__text {
      padding-inline-start: var(--sky-input-pad-x-sm);
    }

    .custom-file-label:empty::after {
      content: "Choose file";
      color: var(--sky-input-color);
    }

    .clear-button:hover {
      color: var(--sky-danger-primary);
    }

    textarea {
      flex: 1 1 auto;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      box-sizing: border-box;
      color: var(--sky-text-primary);
      padding: var(--sky-input-pad-y) var(--sky-input-pad-x);
      resize: none;
      overflow: auto;
      font-family: var(--sky-font);
    }

    textarea[resizable="vertical"] {
      resize: vertical;
    }

    textarea[resizable="both"] {
      resize: both;
    }

    textarea {
      scrollbar-width: thin;
      scrollbar-color: var(--sky-scrollbar-thumb) var(--sky-scrollbar-track);
    }

    textarea::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    textarea::-webkit-scrollbar-thumb {
      background-color: var(--sky-scrollbar-thumb);
      border-radius: var(--sky-radius-full, 9999px);
      transition: background-color var(--sky-duration-normal, 200ms)
        var(--sky-ease-default, ease);
      cursor: grab;
    }

    textarea::-webkit-scrollbar-thumb:hover {
      background-color: var(--sky-scrollbar-thumb-hover);
    }

    textarea::-webkit-scrollbar-track {
      background: var(--sky-scrollbar-track);
    }

    textarea::-webkit-scrollbar-button {
      display: none;
    }

    textarea::placeholder {
      color: var(--sky-text-tertiary);
    }

    .input-wrapper:has(textarea) {
      height: calc((var(--rows)) * 23px);
      align-items: flex-start;
    }
    textarea {
      height: calc((var(--rows)) * 23px);
    }

    @media (prefers-reduced-motion: reduce) {
      .loading-indicator {
        animation: none;
      }

      .input-container:focus-within > .label,
      .input-wrapper,
      .icon-slot,
      .increment-btn,
      .decrement-btn,
      .clear-button,
      input,
      textarea,
      .custom-file-label {
        transition: none;
      }
    }
  `;

  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.autofillGuardActive = this.type === "search";

    if (!this.hasAttribute("tabindex")) {
      this.tabIndex = this.disabled ? -1 : 0;
    }

    if (this.hasUpdated) {
      this._setupIconSlots();
    }
  }

  override disconnectedCallback(): void {
    this._leftIconSlot?.removeEventListener(
      "slotchange",
      this._onLeftIconSlotChange,
    );
    this._rightIconSlot?.removeEventListener(
      "slotchange",
      this._onRightIconSlotChange,
    );
    this._leftIconSlot = null;
    this._rightIconSlot = null;
    super.disconnectedCallback();
  }

  private _setupIconSlots(): void {
    if (!this._leftIconSlot) {
      this._leftIconSlot =
        this.shadowRoot?.querySelector<HTMLSlotElement>(
          'slot[name="icon-left"]',
        ) ?? null;
      this._leftIconSlot?.addEventListener(
        "slotchange",
        this._onLeftIconSlotChange,
      );
    }
    if (!this._rightIconSlot) {
      this._rightIconSlot =
        this.shadowRoot?.querySelector<HTMLSlotElement>(
          'slot[name="icon-right"]',
        ) ?? null;
      this._rightIconSlot?.addEventListener(
        "slotchange",
        this._onRightIconSlotChange,
      );
    }
    this._wireIconSlot("left", this._leftIconSlot);
    this._wireIconSlot("right", this._rightIconSlot);
  }

  private _wireIconSlot(
    side: "left" | "right",
    slot: HTMLSlotElement | null,
  ): void {
    if (!slot) return;
    for (const node of slot.assignedElements({ flatten: true })) {
      if (!(node instanceof HTMLElement)) continue;
      if (this._iconClickWired.has(node)) continue;
      this._iconClickWired.add(node);
      node.addEventListener("click", () => this.handleIconClick(side));
    }
  }

  override firstUpdated(
    _changedProperties: Map<string | number | symbol, unknown>,
  ) {
    super.firstUpdated(_changedProperties);

    // default/reset value
    this.captureDefaultValue(this.value as string);

    // initial sync into form
    this.syncFormValue();
    this.syncValidity();

    this._setupIconSlots();
  }

  override willUpdate(
    changedProperties: Map<string | number | symbol, unknown>,
  ) {
    if (
      changedProperties.has("validations") &&
      Array.isArray(this.validations)
    ) {
      this.validationStatus = this.validations.map(() => false);
    }

    if (changedProperties.has("value") && this.type === "file") {
      if (this.value == null || String(this.value).trim() === "") {
        this.selectedFileName = "";
        this.selectedFiles = [];
        this.totalFileSize = 0;
        this.selectedFileCount = 0;
        this.filePreview = null;
      } else if (!this.selectedFileName) {
        this.selectedFileName = this.value as string;
      }
    }
    if (changedProperties.has("type") && this.type === "search") {
      this.autofillGuardActive = true;
    }
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    // After PresetController hostUpdate — keep host attrs in sync with props.
    this.setAttribute("data-sky-variant", this.variant);
    this.toggleAttribute("data-sky-compact", this.compact);

    if (this.color) {
      this.style.setProperty(
        "--sky-input-color",
        resolveColor(this.color),
      );
    }

    if (changedProperties.has("rows")) {
      const inputWrapper = this.inputWrapperEl;
      if (inputWrapper) {
        inputWrapper.style.setProperty("--rows", String(this.rows));
      }
    }

    if (
      changedProperties.has("value") ||
      changedProperties.has("selectedFiles") ||
      changedProperties.has("selectedFileName") ||
      changedProperties.has("type") ||
      changedProperties.has("multiple") ||
      changedProperties.has("name") ||
      changedProperties.has("disabled")
    ) {
      this.syncFormValue();
    }

    if (
      changedProperties.has("value") ||
      changedProperties.has("selectedFiles") ||
      changedProperties.has("selectedFileName") ||
      changedProperties.has("validationErrors") ||
      changedProperties.has("required") ||
      changedProperties.has("disabled") ||
      changedProperties.has("readonly")
    ) {
      this.syncValidity();
    }
  }

  /** @protected */
  protected override getFormValue(): string | File | FormData | null {
    if (!this.name) return null;

    if (this.type === "file") {
      if (!this.selectedFiles || this.selectedFiles.length === 0) return null;

      if (this.multiple) {
        const fd = new FormData();
        for (const f of this.selectedFiles) fd.append(this.name, f);
        return fd;
      }
      return this.selectedFiles[0];
    }

    return this.value as string ?? "";
  }

  /** @protected */
  protected override setValueFromFormState(
    state: string | File | FormData | null,
  ) {
    // For SkyInput, state restore is typically string
    if (typeof state === "string") {
      this.value = state;
      // for textarea/input UI to refresh, you already use live()
      return;
    }

    // For file state restore: browsers usually won't restore actual File objects for security.
    // So do a safe reset:
    this.value = "";
    this.selectedFileName = "";
    this.selectedFiles = [];
    this.totalFileSize = 0;
    this.selectedFileCount = 0;
    this.filePreview = null;
  }

  /** @protected */
  protected override getValidityAnchor(): HTMLElement | undefined {
    return this;
  }

  /** @protected */
  protected override isEmpty(): boolean {
    if (this.type === "file") {
      return !this.selectedFiles || this.selectedFiles.length === 0;
    }
    return !this.value || this.value.toString().trim() === "";
  }

  /** @protected */
  protected override getCustomErrorMessage(): string {
    if (!this.validationErrors?.length) return "";

    // single message that includes all
    if (this.errorDisplayMode === "all") {
      return this.validationErrors.join("\n");
    }

    // single mode
    return this.validationErrors[0];
  }

  /** @protected */
  protected override getNativeControl(): HTMLElement | null {
    if (this.type === "textarea") {
      return this.shadowRoot?.querySelector(
        "textarea",
      ) as HTMLTextAreaElement | null;
    }
    // for all other types (text/email/password/date/...)
    return this.shadowRoot?.querySelector("input") as HTMLInputElement | null;
  }

  /** @protected */
  protected override onNativeValueMutated(native: any): void {
    // File input value is managed via change handler; do not mirror native.value here.
    if (this.type !== "file") {
      this.value = native?.value ?? "";
    }
    // keep SkyInput custom validations in sync
    this.validateInput();
  }

  /**
   * Resets the input field to its initial state.
   * @public
   */
  reset() {
    this.value = "";
    this.selectedFileName = "";
    this.filePreview = null;
    this.selectedFiles = [];
    this.selectedFileCount = 0;
    this.validationErrors = [];
    this.typingStarted = false;
    this.validationActive = false;
    this.selectedFileCount = 0;
    this.totalFileSize = 0;
  }

  /** @private */
  private get nativeRequiredMessage(): string {
    if (!this.validationActive) return "";
    if (this.disabled) return "";
    if (this.required && this.isEmpty())
      return this.requiredMessage || "This field is required.";
    return "";
  }

  /** @private */
  private getEmittedValue(): string | number {
    if (this.type !== "number") return this.value as string;

    // allow empty to stay empty (important for clearing)
    if (this.value === "" || this.value == null) return "";

    const n = Number(this.value);
    // if user is still typing an invalid numeric token, keep as string
    return Number.isFinite(n) ? n : this.value as string;
  }

  /**
   * Clears the input value and resets typing state.
   * @public
   */
  handleClear() {
    if (this.disabled || this.readonly) {
      return;
    }
    this.reset();
    this.validationActive = true;
    this.validateInput();
    this.dispatchEvent(
      new CustomEvent<SkyInputValueChangedDetail>("value-changed", {
        detail: { value: this.getEmittedValue() },
        bubbles: true,
        composed: true,
      }),
    );
    this.dispatchEvent(
      new CustomEvent<SkyInputValueClearedDetail>("value-cleared", {
        detail: { value: this.getEmittedValue() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Handles the `change` event triggered by the file input element.
   * @param {Event} event - The event object generated by the file input element when files are selected.
   * @public
   */
  handleFileChange(event: Event): void {
    if (this.disabled || this.readonly) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length) {
      const fileList: File[] = Array.from(files);
      this.selectedFiles = fileList;
      this.selectedFileName = fileList
        .map((file: File) => file.name)
        .join(", ");
      this.value = this.selectedFileName;

      this.syncFormValue();
      this.syncValidity();

      const totalSize: number = fileList.reduce(
        (total: number, file: File) => total + file.size,
        0,
      );
      this.totalFileSize = totalSize;

      this.selectedFileCount = fileList.length;

      if (fileList[0].type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          if (e.target) {
            this.filePreview = e.target.result;
          }
        };
        reader.readAsDataURL(fileList[0]);
      } else {
        this.filePreview = null;
      }

      this.reemitNativeEvent("change", event);
      this.reemitNativeEvent("input", event);

      this.dispatchEvent(
        new CustomEvent<SkyInputFileSelectedDetail>("file-selected", {
          detail: {
            files: fileList,
            fileName: this.selectedFileName,
            totalSize,
            fileCount: this.selectedFileCount,
          },
          bubbles: true,
          composed: true,
        }),
      );

      this.validateInput();
    } else {
      this.value = "";
      this.filePreview = null;
      this.totalFileSize = 0;
      this.selectedFileCount = 0;
      this.selectedFiles = [];
      this.validateInput();
    }
  }

  /**
   * Handles the keydown event for the input component.
   * @param event - The keyboard event triggered by the user.
   * @private
   */
  private handleKeydown(event: KeyboardEvent) {
    if (this.disabled || this.readonly) {
      return;
    }
    if (event.key === "Enter" && this.type === "file") {
      event.preventDefault();
      event.stopPropagation();
      this.triggerNativeDropdown();
    }
  }

  /**
   * Gets the current file preview.
   * @returns The preview of the file, as stored in `filePreview`.
   * @public
   */
  get preview() {
    return this.filePreview;
  }

  /**
   * Handles the input event for the input field.
   * @param {Event} event - The input event triggered by user interaction.
   * @public
   */
  handleInput(event: Event) {
    event.stopPropagation();

    if (this.disabled || this.readonly) {
      event.preventDefault();
      return;
    }

    this.value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;

    this.syncFormValue();
    this.syncValidity();

    if (!this.typingStarted) this.typingStarted = true;

    // Live-validate only after the field has been blurred once (or submit activated).
    if (this.validationActive) {
      this.validateInput();
    }

    this.reemitNativeEvent("input", event);

    this.dispatchEvent(
      new CustomEvent<SkyInputValueChangedDetail>("value-changed", {
        detail: { value: this.getEmittedValue() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Handles the focus event for the input field.
   * @public
   */
  handleFocus(event: Event) {
    event.stopPropagation();
    this.releaseAutofillGuard();
    this.focused = true;
    this.reemitNativeEvent("focus", event);
  }

  /** Chrome may autofill saved usernames into filter fields; readonly until interaction blocks that. */
  private releaseAutofillGuard(): void {
    if (!this.autofillGuardActive) return;
    this.autofillGuardActive = false;
  }

  private handleAutofillGuardPointerDown(event: Event): void {
    this.releaseAutofillGuard();
    event.stopPropagation();
  }

  /** @protected */
  protected override onFormReset() {
    this.setValueFromFormState(this.defaultValue);

    this.validationActive = false;
    this.invalid = false;
    this.typingStarted = false;
    this.validationErrors = [];
    this.validationStatus = [];

    if (this.type === "file") {
      this.selectedFiles = [];
      this.selectedFileName = "";
      this.totalFileSize = 0;
      this.selectedFileCount = 0;
      this.filePreview = null;
    }

    if (this.type === "search") {
      this.autofillGuardActive = true;
    }
  }

  /**
   * Handles the blur event for the input field.
   * @public
   */
  handleBlur(event: Event) {
    event.stopPropagation();
    this.validationActive = true;
    this.focused = false;
    this.reemitNativeEvent("blur", event);
  }

  /** @protected */
  protected override validateForForm(): string {
    // run your `this.validations` against current value even if validationActive is false
    const payload =
      this.type === "file" ? (this.selectedFiles ?? []) : (this.value ?? "");

    for (const v of this.validations ?? []) {
      const r = v(payload as string | File[]);
      if (r !== true) return typeof r === "string" ? r : "Invalid value.";
    }

    return "";
  }

  /**
   * Validates the input value against the configured validation rules.
   * @returns {boolean} True if validation passes, false otherwise.
   * @public
   */
  validateInput(): boolean {
    const nextErrors: string[] = [];
    const nextStatus: boolean[] = [];

    const isFileInput = this.type === "file";
    const isEmpty = isFileInput
      ? !this.selectedFiles || this.selectedFiles.length === 0
      : !this.value || this.value.toString().trim() === "";

    // 1) If empty + required -> let SkyFormControlBase handle native required
    //    (do NOT push required into validationErrors)
    if (this.required && isEmpty) {
      this.syncValidity(); // will set valueMissing
    }

    // 2) Run custom validations only if there is something to validate
    if (Array.isArray(this.validations) && this.validations.length) {
      const payload: string | File[] = isFileInput
        ? (this.selectedFiles ?? [])
        : (this.value as string ?? "");

      for (let index = 0; index < this.validations.length; index++) {
        const validation = this.validations[index];
        try {
          if (typeof validation !== "function") {
            throw new Error(`Validation at index ${index} must be a function.`);
          }

          const result = validation(payload);

          if (result !== true) {
            nextErrors.push(
              typeof result === "string"
                ? result
                : `Validation ${index} failed.`,
            );
            nextStatus.push(false);
          } else {
            nextStatus.push(true);
          }
        } catch (error) {
          const message =
            typeof error === "object" && error !== null && "message" in error
              ? String((error as any).message)
              : typeof error === "string"
                ? error
                : "Unknown error";

          nextErrors.push(`Error in validation ${index}: ${message}`);
          nextStatus.push(false);
        }
      }
    }

    const errorsChanged = !samePrimitiveArray(this.validationErrors, nextErrors);
    const statusChanged = !samePrimitiveArray(this.validationStatus, nextStatus);
    if (errorsChanged) this.validationErrors = nextErrors;
    if (statusChanged) this.validationStatus = nextStatus;

    if (errorsChanged) {
      this.dispatchEvent(
        new CustomEvent<SkyInputValidationErrorDetail>("validation-error", {
          detail: { errors: this.validationErrors },
          bubbles: true,
          composed: true,
        }),
      );
    }

    this.syncValidity();

    const isValid = nextErrors.length === 0;
    if (this.invalid !== !isValid) this.invalid = !isValid;

    return isValid;
  }

  /**
   * Retrieves validation error messages based on the configured error display mode.
   * @returns {Array} - An array of error messages to display.
   * @private
   */
  private getErrorMessages() {
    switch (this.errorDisplayMode) {
      case "single":
        return this.validationErrors.slice(0, 1);
      case "all":
        return this.validationErrors;
      case "none":
      default:
        return [];
    }
  }

  /**
   * Handles click events for the left or right icon slots.
   * @param {String} position - The position of the icon ("left" or "right").
   * @param {Event|null} event - The original event triggered by the click (optional).
   * @public
   */
  handleIconClick(position: "left" | "right", event: Event | null = null) {
    this.dispatchEvent(
      new CustomEvent<SkyInputIconClickDetail>(`${position}-icon-click`, {
        detail: {
          position,
          eventType: event ? event.type : null,
          eventData: event,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Formats a file size in bytes into a human-readable string.
   * @param {number} bytes - The file size in bytes.
   * @returns {string} - The formatted file size string.
   * @public
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Increments the input value by the specified step, respecting min/max constraints.
   * @private
   */
  private incrementValue() {
    if (this.disabled || this.readonly) return;
    const currentValue = parseFloat(this.value as string) || 0;
    const step = parseFloat(this.step ?? "") || 1;
    const max = this.max ? parseFloat(this.max) : Infinity;

    if (currentValue + step <= max) {
      this.value = (currentValue + step).toString();
      this.validateInput();
      this.dispatchEvent(
        new CustomEvent<SkyInputValueChangedDetail>("value-changed", {
          detail: { value: this.getEmittedValue() },
        }),
      );
    }
    this.refocusInput();
  }

  /**
   * Decrements the input value by the specified step, respecting min/max constraints.
   * @private
   */
  private decrementValue() {
    if (this.disabled || this.readonly) return;
    const currentValue = parseFloat(this.value as string) || 0;
    const step = parseFloat(this.step ?? "") || 1;
    const min = this.min ? parseFloat(this.min) : -Infinity;

    if (currentValue - step >= min) {
      this.value = (currentValue - step).toString();
      this.validateInput();
      this.dispatchEvent(
        new CustomEvent<SkyInputValueChangedDetail>("value-changed", {
          detail: { value: this.getEmittedValue() },
        }),
      );
    }
    this.refocusInput();
  }

  /**
   * Renders validation error messages if applicable.
   * @returns {TemplateResult|null} - The rendered error messages or null.
   * @private
   */
  private renderValidationErrors() {

    if (!this.showErrors || !this.validationActive) return null;

    const requiredMsg = this.nativeRequiredMessage;
    const customMsgs = this.getErrorMessages(); // already respects errorDisplayMode for custom

    let msgs: string[] = [];

    if (this.errorDisplayMode === "single") {
      // Required message takes precedence over custom errors.
      if (requiredMsg) msgs = [requiredMsg];
      else if (customMsgs.length) msgs = [customMsgs[0]];
    } else {
      msgs = requiredMsg ? [requiredMsg, ...customMsgs] : customMsgs;
    }

    if (!msgs.length) return null;

    const errId = `${this.id || "sky-input"}-errors`;

    return html`
      <div class="progress-message">
        <ul id=${errId} class="error-list" part="error-list">
          ${msgs.map((m) => html`<li class="error" part="error">${m}</li>`)}
        </ul>
      </div>
    `;
  }

  /**
   * Toggles the visibility of the password input.
   * @public
   */
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  public focusInput(): void {
    const input = this.nativeInputEl;
    if (
      input &&
      typeof (input as HTMLInputElement | HTMLTextAreaElement).focus === "function"
    ) {
      (input as HTMLInputElement | HTMLTextAreaElement).focus();
    }
  }

  /**
   * Handles click events on the input wrapper.
   * @param {Event} event - The original click event.
   * @private
   */
  private handleWrapperClick(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    const input = this.nativeInputEl;
    if (
      input &&
      typeof (input as HTMLInputElement | HTMLTextAreaElement).focus ===
      "function"
    ) {
      (input as HTMLInputElement | HTMLTextAreaElement).focus();
    }
  }

  /** @private */
  private get isInvalid(): boolean {
    return (
      this.matches(":invalid") ||
      (this.validationActive && this.validationErrors.length > 0)
    );
  }

  /** @private */
  private get describedById(): string | undefined {
    return (
      this.ariaDescribedby ??
      (this.showErrors ? `${this.id || "sky-input"}-errors` : undefined)
    );
  }

  /**
   * Refocuses the input element.
   * @private
   */
  private refocusInput() {
    const el = this.shadowRoot?.querySelector(
      "input",
    ) as HTMLInputElement | null;
    el?.focus({ preventScroll: true });
  }

  /**
   * Keeps the input focused when interacting with related controls.
   * @param {Event} e - The original event.
   * @private
   */
  private keepInputFocused = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.refocusInput();
  };

  /**
   * Triggers the native dropdown for date/time/file inputs.
   * @private
   */
  private triggerNativeDropdown() {
    if (this.disabled || this.readonly) return;
    const input = this.shadowRoot?.querySelector(
      'input[type="date"], input[type="time"], input[type="week"], input[type="month"], input[type="datetime-local"], input[type="file"]',
    ) as HTMLInputElement | null;

    if (input) {
      if (this.type === "file") {
        input.click();
      } else {
        this.showPicker();
      }
    }
  }

  /**
   * Filter/search inputs (`type="search"`) are rendered as plain text fields so
   * Chrome does not treat them as login username targets. Credential autofill is
   * further blocked with readonly-until-focus (Chrome ignores autocomplete tokens).
   */
  private isFilterSearchInput(): boolean {
    return this.type === "search";
  }

  private nativeAutocomplete(): string {
    if (this.isFilterSearchInput()) {
      return "off";
    }
    return this.autocomplete;
  }

  private nativeTextInputType(isPassword: boolean): string {
    if (isPassword && this.showPassword) return "text";
    if (this.isFilterSearchInput()) return "text";
    return this.type;
  }

  private nativeInputMode(): string | undefined {
    if (this.inputmode) return this.inputmode;
    if (this.isFilterSearchInput()) return "search";
    return undefined;
  }

  private nativeEnterKeyHint():
    | "enter"
    | "done"
    | "go"
    | "next"
    | "previous"
    | "search"
    | "send"
    | undefined {
    if (this.enterkeyhint) {
      return this.enterkeyhint as
        | "enter"
        | "done"
        | "go"
        | "next"
        | "previous"
        | "search"
        | "send";
    }
    if (this.isFilterSearchInput()) return "search";
    return undefined;
  }

  private nativeInputRole(): string | undefined {
    if (this.inputRole) return this.inputRole;
    if (this.type === "search") return "searchbox";
    return undefined;
  }

  private affixColorStyle(color: string) {
    const trimmed = String(color ?? "").trim();
    if (!trimmed) return undefined;
    return { color: resolveColor(trimmed) };
  }

  private renderInputAffix(kind: "prefix" | "suffix") {
    const value = kind === "prefix" ? this.prefix : this.suffix;
    if (!String(value ?? "").length) return nothing;
    const color = kind === "prefix" ? this.prefixColor : this.suffixColor;
    const style = this.affixColorStyle(color);
    return html`<span
      class="input-affix ${kind}"
      part="input-${kind}"
      style=${style ? styleMap(style) : nothing}
      >${value}</span
    >`;
  }

  override render() {
    const isFileInput = this.type === "file";
    const isTextarea = this.type === "textarea";
    const isNumberInput = this.type === "number";
    const isPassword = this.type === "password";
    const supportsInlineAffixes = !isFileInput && !isTextarea;

    const hasLeftSlot = !!this.querySelector('[slot="icon-left"]');
    const hasRightSlot = !!this.querySelector('[slot="icon-right"]');

    let autoIcon: unknown = null;
    if (this.type === "date")
      autoIcon = html`<sky-icon icon="ion:calendar"></sky-icon>`;
    else if (this.type === "time")
      autoIcon = html`<sky-icon icon="ion:time"></sky-icon>`;
    else if (this.type === "week")
      autoIcon = html`<sky-icon icon="ion:calendar"></sky-icon>`;
    else if (this.type === "month")
      autoIcon = html`<sky-icon icon="ion:calendar"></sky-icon>`;
    else if (this.type === "datetime-local")
      autoIcon = html`<sky-icon icon="ion:time"></sky-icon>`;

    const showFilePreview = this.filePreview && (hasLeftSlot || hasRightSlot);
    const showAutoIconInLeft = this.autofillIconSlot === "left" && autoIcon;
    const showAutoIconInRight = this.autofillIconSlot === "right" && autoIcon;

    const ImagePreview = () => html`
      <img
        src=${ifDefined(
      typeof this.filePreview === "string" ? this.filePreview : undefined,
    )}
        alt="Preview"
        style="width:100%;height:100%;object-fit:cover;border-radius:12px;"
      />
    `;

    const LeftIconSlot = () => html`
      <div
        class="icon-slot left ${this.focused ? "focused" : ""}"
        part="icon-slot icon-slot-left"
        ?filled=${Boolean(showFilePreview && hasLeftSlot) ||
      Boolean(showAutoIconInLeft) ||
      hasLeftSlot}
        @click=${() => {
        if (isFileInput || showAutoIconInLeft) this.triggerNativeDropdown();
      }}
      >
        ${showFilePreview && hasLeftSlot
        ? ImagePreview()
        : showAutoIconInLeft
          ? autoIcon
          : html`<slot name="icon-left"></slot>`}
      </div>
    `;

    const RightIconSlot = () =>
      this.loading
        ? html`<div
            class="loading-indicator"
            part="loading-indicator"
            ?compact=${this.compact}
          ></div>`
        : html`
            <div
              class="icon-slot right ${this.focused ? "focused" : ""}"
              part="icon-slot icon-slot-right"
              ?filled=${Boolean(showFilePreview && hasRightSlot) ||
          Boolean(showAutoIconInRight) ||
          hasRightSlot}
              @click=${() => {
            if (isFileInput || showAutoIconInRight)
              this.triggerNativeDropdown();
          }}
            >
              ${showFilePreview && hasRightSlot
            ? ImagePreview()
            : showAutoIconInRight
              ? autoIcon
              : html`<slot name="icon-right"></slot>`}
            </div>
          `;

    const FileControl = () => html`
      <label
        class="custom-file-label"
        part="custom-file-label"
        for="fileInput"
        ?disabled=${this.disabled}
      >
        ${this.renderInputAffix("prefix")}
        <span
          class="custom-file-label__text"
          part="custom-file-label-text"
          ?data-filled=${Boolean(this.selectedFileName)}
        >
          ${this.selectedFileName || this.placeholder || "Choose file"}
        </span>
        ${this.renderInputAffix("suffix")}
      </label>

      <input
        id="fileInput"
        type="file"
        @change=${this.handleFileChange}
        ?disabled=${this.disabled}
        accept=${this.accept || ""}
        ?multiple=${this.multiple}
        .required=${this.required}
        @keydown=${this.handleKeydown}
        .dir=${this.dir}
        name=${ifDefined(this.name || undefined)}
        .autocomplete=${this.nativeAutocomplete()}
        .autocapitalize=${this.autocapitalize}
        spellcheck=${this.spellcheck}
        ?autofocus=${this.autofocus}
        inputmode=${ifDefined(this.inputmode || undefined)}
        enterkeyhint=${ifDefined(
      this.enterkeyhint as
      | "enter"
      | "done"
      | "go"
      | "next"
      | "previous"
      | "search"
      | "send"
      | undefined,
    )}
        list=${ifDefined(this.list || undefined)}
        capture=${ifDefined(this.capture ? "environment" : undefined)}
        .aria-invalid=${String(this.isInvalid)}
        .aria-required=${String(this.required)}
        aria-label=${ifDefined(this.ariaLabel || undefined)}
        aria-labelledby=${ifDefined(this.ariaLabelledby || undefined)}
        aria-describedby=${ifDefined(this.describedById)}
      />
    `;

    const TextareaControl = () => html`
      <textarea
        .value=${live(this.value)}
        @input=${this.handleInput}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        ?compact=${this.compact}
        ?disabled=${this.disabled}
        .required=${this.required}
        .rows=${this.rows}
        placeholder=${this.placeholder}
        resizable=${this.resizable || "none"}
        style="width:100%;border:none;outline:none;"
        .autocapitalize=${this.autocapitalize}
        ?spellcheck=${this.spellcheck}
        name=${ifDefined(this.name || undefined)}
        .autocomplete=${this.nativeAutocomplete()}
        inputmode=${ifDefined(this.inputmode || undefined)}
        enterkeyhint=${ifDefined(
      this.enterkeyhint as
      | "enter"
      | "done"
      | "go"
      | "next"
      | "previous"
      | "search"
      | "send"
      | undefined,
    )}
        minlength=${ifDefined(this.minlength ?? undefined)}
        maxlength=${ifDefined(this.maxlength ?? undefined)}
        ?autofocus=${this.autofocus}
        .aria-invalid=${String(this.isInvalid)}
        .aria-required=${String(this.required)}
        aria-label=${ifDefined(this.ariaLabel || undefined)}
        aria-labelledby=${ifDefined(this.ariaLabelledby || undefined)}
        aria-describedby=${ifDefined(this.describedById)}
        role=${ifDefined(this.nativeInputRole() as
      | "textbox"
      | "searchbox"
      | "combobox"
      | "listbox"
      | "menuitem"
      | "option"
      | "slider"
      | "spinbutton"
      | "treeitem"
      | undefined)}
      ></textarea>
    `;

    const TextInputControl = () => html`
      <input
        .type=${this.nativeTextInputType(isPassword)}
        .value=${live(this.value)}
        @input=${this.handleInput}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        @pointerdown=${this.handleAutofillGuardPointerDown}
        ?compact=${this.compact}
        ?disabled=${this.disabled}
        .required=${this.required}
        .readOnly=${this.readonly || this.autofillGuardActive}
        style="appearance:none;pointer-events:auto;"
        placeholder=${this.placeholder}
        name=${ifDefined(this.name || undefined)}
        .autocomplete=${this.nativeAutocomplete()}
        .autocapitalize=${this.autocapitalize}
        spellcheck=${this.spellcheck}
        inputmode=${ifDefined(this.nativeInputMode())}
        enterkeyhint=${ifDefined(this.nativeEnterKeyHint())}
        data-form-type=${this.isFilterSearchInput() ? "other" : nothing}
        ?data-1p-ignore=${this.isFilterSearchInput()}
        ?data-lpignore=${this.isFilterSearchInput()}
        list=${ifDefined(this.list || undefined)}
        pattern=${ifDefined(this.pattern || undefined)}
        minlength=${ifDefined(this.minlength ?? undefined)}
        maxlength=${ifDefined(this.maxlength ?? undefined)}
        size=${ifDefined(this.size ?? undefined)}
        step=${ifDefined(this.step !== null ? Number(this.step) : undefined)}
        min=${ifDefined(this.min ?? undefined)}
        max=${ifDefined(this.max ?? undefined)}
        ?autofocus=${this.autofocus}
        .aria-invalid=${String(this.isInvalid)}
        .aria-required=${String(this.required)}
        aria-label=${ifDefined(this.ariaLabel || undefined)}
        aria-labelledby=${ifDefined(this.ariaLabelledby || undefined)}
        aria-describedby=${ifDefined(this.describedById)}
        role=${ifDefined(this.nativeInputRole() as
      | "textbox"
      | "searchbox"
      | "combobox"
      | "listbox"
      | "menuitem"
      | "option"
      | "slider"
      | "spinbutton"
      | "treeitem"
      | undefined)}
      />
    `;

    const NumberButtons = () =>
      isNumberInput && this.showNumberButtons
        ? html`
            <div class="number-btn">
              <button
                class="decrement-btn"
                type="button"
                tabindex="-1"
                @mousedown=${this.keepInputFocused}
                @pointerdown=${this.keepInputFocused}
                @click=${(e: Event) => {
            e.stopPropagation();
            this.decrementValue();
          }}
                aria-label="Decrease value"
              >
                <sky-icon icon="ion:minus"></sky-icon>
              </button>
              <button
                class="increment-btn"
                type="button"
                tabindex="-1"
                @mousedown=${this.keepInputFocused}
                @pointerdown=${this.keepInputFocused}
                @click=${(e: Event) => {
            e.stopPropagation();
            this.incrementValue();
          }}
                aria-label="Increase value"
              >
                <sky-icon icon="ion:add"></sky-icon>
              </button>
            </div>
          `
        : null;

    const PasswordToggle = () =>
      isPassword && this.showPasswordToggle
        ? html`
            <sky-icon
              class="toggle-password"
              part="toggle-password"
              ?compact=${this.compact}
              icon=${this.showPassword
            ? this.passwordVisibleIcon
            : this.passwordHiddenIcon}
              @click=${this.togglePasswordVisibility}
            ></sky-icon>
          `
        : null;

    const ClearButton = () =>
      this.clearable &&
        (this.value || this.selectedFileName || this.selectedFileCount > 0)
        ? html`
            <sky-icon
              class="clear-button"
              part="clear-button"
              ?compact=${this.compact}
              @click=${this.handleClear}
              ?loading=${this.loading}
              icon="ion:close"
            ></sky-icon>
          `
        : null;

    const FileInfo = () =>
      isFileInput && this.totalFileSize
        ? html`
            <div class="file-info" part="file-info">
              <span>
                ${this.selectedFileCount}
                file${this.selectedFileCount !== 1 ? "s selected" : " selected"} </span
              >,
              <span>${this.formatFileSize(this.totalFileSize)}</span>
            </div>
          `
        : null;

    const Control = () =>
      isFileInput
        ? FileControl()
        : isTextarea
          ? TextareaControl()
          : TextInputControl();

    const isFieldset = this.variant === "fieldset";
    const isInside = this.variant === "inside";
    const labelEl = this.label
      ? isFieldset
        ? html`<legend class="label" part="label"><span class="legend-text">${this.label}</span></legend>`
        : html`<div class="label" part="label">${this.label}</div>`
      : null;

    const fieldControls = html`
      ${supportsInlineAffixes ? this.renderInputAffix("prefix") : nothing}
      ${Control()}
      ${supportsInlineAffixes ? this.renderInputAffix("suffix") : nothing}
      ${NumberButtons()} ${PasswordToggle()}
      ${ClearButton()}
    `;

    const defaultFieldContent = html`
      ${LeftIconSlot()}
      ${supportsInlineAffixes ? this.renderInputAffix("prefix") : nothing}
      ${Control()}
      ${supportsInlineAffixes ? this.renderInputAffix("suffix") : nothing}
      ${NumberButtons()} ${PasswordToggle()}
      ${ClearButton()} ${RightIconSlot()}
    `;

    const insideFieldContent = html`
      ${LeftIconSlot()}
      <div class="input-inside-main">
        ${labelEl}
        <div class="input-inside-row">${fieldControls}</div>
      </div>
      ${RightIconSlot()}
    `;

    return html`
      <div
        class="input-container"
        @click=${this.handleWrapperClick}
        part="input-container"
      >
        ${isFieldset || isInside ? nothing : labelEl}
        ${isFieldset
          ? html`<fieldset
              class="input-wrapper"
              part="input-wrapper"
              ?compact=${this.compact}
              ?loading=${this.loading}
            >
              ${labelEl}
              <div class="input-fieldset-body">
                ${LeftIconSlot()}
                ${supportsInlineAffixes ? this.renderInputAffix("prefix") : nothing}
                ${Control()}
                ${supportsInlineAffixes ? this.renderInputAffix("suffix") : nothing}
                ${NumberButtons()} ${PasswordToggle()}
                ${ClearButton()} ${RightIconSlot()}
              </div>
            </fieldset>`
          : html`<div
              class="input-wrapper"
              part="input-wrapper"
              ?compact=${this.compact}
              ?loading=${this.loading}
            >
              ${isInside ? insideFieldContent : defaultFieldContent}
            </div>`}

        ${FileInfo()} ${this.renderValidationErrors()}
      </div>
    `;
  }
}
